import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationSearchInput from '../../components/LocationSearchInput';
import { 
  Sprout, 
  ArrowLeft, 
  Upload, 
  Image as ImageIcon, 
  X, 
  CheckCircle2, 
  Building2, 
  MapPin, 
  Calendar, 
  Users, 
  Check, 
  Gift, 
  Truck
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

interface ProjectItem {
  project_id: number;
  title: string;
  location?: string;
  funding_goal?: number;
}

interface CategoryItem {
  category_id: number;
  name: string;
}

const CreateFarmProject: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('token');

  // Creation Mode: 'existing' | 'scratch'
  const [creationMode, setCreationMode] = useState<'existing' | 'scratch'>('existing');

  // Existing Projects Dropdown List
  const [projectsList, setProjectsList] = useState<ProjectItem[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Scratch Base Project Form Fields
  const [title, setTitle] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [fullDesc, setFullDesc] = useState('');
  const [location, setLocation] = useState('');
  const [locationLat, setLocationLat] = useState(0);
  const [locationLon, setLocationLon] = useState(0);
  const [fundingGoal, setFundingGoal] = useState('');

  // Farm Sponsorship Specifications
  const [capacity, setCapacity] = useState('150');
  const [estimatedHarvest, setEstimatedHarvest] = useState('');
  const [benefitOptions, setBenefitOptions] = useState<('produce' | 'rita_credit')[]>(['produce', 'rita_credit']);
  const [maxBeneficiaries, setMaxBeneficiaries] = useState('5');

  // Images Upload State
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  // UI Status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load existing base projects for dropdown
  useEffect(() => {
    if (!token) return;
    setLoadingProjects(true);
    fetch(`${API_BASE_URL}/admin/get-all-project`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((payload) => {
        if (payload.success && Array.isArray(payload.data)) {
          setProjectsList(payload.data);
          if (payload.data.length > 0) {
            setSelectedProjectId(String(payload.data[0].project_id));
          }
        }
      })
      .catch((err) => console.error('Failed to load projects dropdown:', err))
      .finally(() => setLoadingProjects(false));
  }, [token]);

  // Handle Image Selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter((file) => file.type.startsWith('image/'));
    setSelectedImages((prev) => [...prev, ...files]);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleBenefitOption = (opt: 'produce' | 'rita_credit') => {
    if (benefitOptions.includes(opt)) {
      if (benefitOptions.length > 1) {
        setBenefitOptions(benefitOptions.filter((o) => o !== opt));
      }
    } else {
      setBenefitOptions([...benefitOptions, opt]);
    }
  };

  const selectedProjectObj = projectsList.find((p) => String(p.project_id) === selectedProjectId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setFeedback({ type: 'error', text: 'Admin authentication missing. Please login again.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      let targetProjectId = Number(selectedProjectId);

      // STEP 1: If Scratch Mode, create Base Project with Agriculture Category automatically
      if (creationMode === 'scratch') {
        if (!title.trim()) throw new Error('Farm Project Title is required.');

        // Find Agriculture Category
        let categoryId = 1;
        try {
          const catRes = await fetch(`${API_BASE_URL}/admin/get-all-category`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (catRes.ok) {
            const catPayload = await catRes.json();
            if (catPayload.success && Array.isArray(catPayload.data)) {
              const agriCat = catPayload.data.find(
                (c: CategoryItem) => c.name.toLowerCase().includes('agri') || c.name.toLowerCase().includes('farm')
              );
              if (agriCat) categoryId = agriCat.category_id;
              else if (catPayload.data.length > 0) categoryId = catPayload.data[0].category_id;
            }
          }
        } catch (cErr) {
          console.error('Category fetch error, defaulting to 1:', cErr);
        }

        // Create Base Project
        const createProjRes = await fetch(`${API_BASE_URL}/admin/create-project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: title.trim(),
            short_description: shortDesc.trim() || `${title.trim()} Farm Project for Diaspora Sponsorship.`,
            full_description: fullDesc.trim() || `${title.trim()} - Commercial agriculture project in ${location}.`,
            category_id: categoryId,
            location: location.trim() || 'Nigeria',
            latitude: locationLat,
            longitude: locationLon,
            funding_goal: Number(fundingGoal) || 15000000,
            expected_roi_percentage: 24.5,
            roi_duration_months: 12,
            currency_code: 'NGN',
            status: 'active',
            rules: {
              auto_close_on_goal: true,
              min_allowed_investment: 100,
              max_allowed_investment: 1000000
            }
          })
        });

        const projPayload = await createProjRes.json();
        if (!projPayload.success || !projPayload.data?.project_id) {
          throw new Error(projPayload.message || 'Failed to create base project.');
        }

        targetProjectId = projPayload.data.project_id;
      }

      if (!targetProjectId) {
        throw new Error('Please select an existing project or enter details for a new project.');
      }

      // STEP 2: Configure Farm Project Specs
      const farmRes = await fetch(`${API_BASE_URL}/admin/farm-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          project_id: targetProjectId,
          sponsorship_capacity: Number(capacity) || 150,
          benefit_options: benefitOptions,
          max_beneficiaries_per_sponsorship: Number(maxBeneficiaries) || 5,
          estimated_harvest_at: estimatedHarvest ? new Date(estimatedHarvest).toISOString() : null,
          is_sponsorship_open: true
        })
      });

      const farmPayload = await farmRes.json();
      if (!farmPayload.success) {
        throw new Error(farmPayload.message || 'Failed to configure farm project.');
      }

      // STEP 3: Upload Images if provided
      if (selectedImages.length > 0) {
        try {
          const formData = new FormData();
          formData.append('project_id', String(targetProjectId));
          selectedImages.forEach((img) => formData.append('images', img));

          await fetch(`${API_BASE_URL}/admin/add-project-images`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData
          });
        } catch (imgErr) {
          console.error('Image upload warning:', imgErr);
        }
      }

      setFeedback({ type: 'success', text: 'Farm Project created and configured successfully!' });

      setTimeout(() => {
        navigate('/admin/farm-operations');
      }, 1500);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err instanceof Error ? err.message : 'An error occurred while saving farm project.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Top Header & Breadcrumb */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/farm-operations')}
            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-extrabold text-xs rounded-full uppercase flex items-center gap-1">
                <Sprout className="w-3.5 h-3.5" /> Agriculture Category Auto-Applied
              </span>
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900 mt-1">Create & Configure Farm Project</h1>
          </div>
        </div>

        <button
          onClick={() => navigate('/admin/farm-operations')}
          className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl"
        >
          Cancel & Back to Console
        </button>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-xl text-sm font-bold flex items-center gap-2 ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <X className="w-5 h-5 text-red-600" />}
          <span>{feedback.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SECTION 1: CREATION MODE SELECTOR */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" /> 1. Select Farm Project Creation Method
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div
              onClick={() => setCreationMode('existing')}
              className={`cursor-pointer p-5 rounded-2xl border-2 transition-all flex items-start gap-4 ${
                creationMode === 'existing'
                  ? 'border-emerald-600 bg-emerald-50/40 shadow-sm'
                  : 'border-gray-100 hover:border-gray-200 bg-white'
              }`}
            >
              <div className={`p-3 rounded-xl ${creationMode === 'existing' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-extrabold text-sm text-gray-900">Choose Existing Base Project</h4>
                <p className="text-xs text-gray-500 mt-1">Select from dropdown of all projects already in system database.</p>
              </div>
            </div>

            <div
              onClick={() => setCreationMode('scratch')}
              className={`cursor-pointer p-5 rounded-2xl border-2 transition-all flex items-start gap-4 ${
                creationMode === 'scratch'
                  ? 'border-emerald-600 bg-emerald-50/40 shadow-sm'
                  : 'border-gray-100 hover:border-gray-200 bg-white'
              }`}
            >
              <div className={`p-3 rounded-xl ${creationMode === 'scratch' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                <Sprout className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-extrabold text-sm text-gray-900">+ Create New Farm Project from Scratch</h4>
                <p className="text-xs text-gray-500 mt-1">1-Click form that automatically sets Category to Agriculture!</p>
              </div>
            </div>
          </div>

          {/* MODE A: EXISTING PROJECT DROPDOWN */}
          {creationMode === 'existing' && (
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <label className="block text-xs font-extrabold text-gray-700">
                Select Base Project from System Dropdown *:
              </label>
              {loadingProjects ? (
                <div className="p-3 bg-gray-50 text-gray-500 text-xs rounded-xl animate-pulse">Loading base projects...</div>
              ) : projectsList.length > 0 ? (
                <select
                  required
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-xl bg-white font-semibold text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">-- Choose Existing Base Project --</option>
                  {projectsList.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.title} (ID #{p.project_id}) {p.location ? `- ${p.location}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-amber-50 text-amber-800 text-xs font-semibold rounded-xl">
                  No existing base projects found. Please switch to "+ Create New Farm Project from Scratch".
                </div>
              )}

              {selectedProjectObj && (
                <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-100 flex items-center justify-between text-xs">
                  <div>
                    <p className="font-bold text-gray-900">{selectedProjectObj.title}</p>
                    <p className="text-gray-500">{selectedProjectObj.location ?? 'Nigeria'}</p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-600 text-white font-extrabold rounded-full">
                    Selected Base ID #{selectedProjectObj.project_id}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* MODE B: NEW SCRATCH FARM FORM */}
          {creationMode === 'scratch' && (
            <div className="pt-4 border-t border-gray-100 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-extrabold text-gray-700 mb-1">Farm Project Title *:</label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Oyo State Layer Poultry & Egg Farm 2026"
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <LocationSearchInput
                    label="Location *:"
                    value={location}
                    required
                    placeholder="e.g. Ibadan, Oyo State, Nigeria"
                    onChange={(loc) => {
                      setLocation(loc.label);
                      setLocationLat(loc.lat);
                      setLocationLon(loc.lon);
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-extrabold text-gray-700 mb-1">Funding Goal (₦) *:</label>
                  <input
                    type="number"
                    required
                    value={fundingGoal}
                    onChange={(e) => setFundingGoal(e.target.value)}
                    placeholder="e.g. 15000000"
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-extrabold text-gray-700 mb-1">Short Description:</label>
                  <input
                    type="text"
                    value={shortDesc}
                    onChange={(e) => setShortDesc(e.target.value)}
                    placeholder="e.g. High-yield 10,000-layer automated poultry egg and broiler farm in Ibadan."
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-extrabold text-gray-700 mb-1">Full Description:</label>
                  <textarea
                    rows={3}
                    value={fullDesc}
                    onChange={(e) => setFullDesc(e.target.value)}
                    placeholder="Provide full operational breakdown of the farm project..."
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 2: FARM SPONSORSHIP SPECIFICATIONS */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sprout className="w-5 h-5 text-emerald-600" /> 2. Farm Sponsorship Parameters & Capacity
          </h3>

          <div className="grid sm:grid-cols-3 gap-4 text-xs font-bold">
            <div>
              <label className="block text-gray-700 mb-1">Sponsorship Limit (Capacity Units) *:</label>
              <input
                type="number"
                required
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="e.g. 150 Units"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Total slots available for diaspora sponsors.</p>
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Estimated Harvest Date:</label>
              <input
                type="date"
                value={estimatedHarvest}
                onChange={(e) => setEstimatedHarvest(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Expected harvest completion date.</p>
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Max Beneficiaries per Sponsorship:</label>
              <input
                type="number"
                min={1}
                max={10}
                value={maxBeneficiaries}
                onChange={(e) => setMaxBeneficiaries(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Max nominated family members per sponsor.</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-extrabold text-gray-700 mb-2">Allowed Beneficiary Benefit Options:</label>
            <div className="flex flex-wrap gap-4">
              <label
                onClick={() => toggleBenefitOption('produce')}
                className={`cursor-pointer px-4 py-3 rounded-xl border-2 flex items-center gap-2 text-xs font-extrabold transition-all ${
                  benefitOptions.includes('produce')
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 text-gray-500'
                }`}
              >
                <Truck className="w-4 h-4 text-emerald-600" />
                <span>Doorstep Produce Delivery</span>
                {benefitOptions.includes('produce') && <Check className="w-4 h-4 text-emerald-600 ml-1" />}
              </label>

              <label
                onClick={() => toggleBenefitOption('rita_credit')}
                className={`cursor-pointer px-4 py-3 rounded-xl border-2 flex items-center gap-2 text-xs font-extrabold transition-all ${
                  benefitOptions.includes('rita_credit')
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 text-gray-500'
                }`}
              >
                <Gift className="w-4 h-4 text-emerald-600" />
                <span>Rita.ng Shopping Credit</span>
                {benefitOptions.includes('rita_credit') && <Check className="w-4 h-4 text-emerald-600 ml-1" />}
              </label>
            </div>
          </div>
        </div>

        {/* SECTION 3: IMAGE UPLOAD SECTION */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-emerald-600" /> 3. Farm Project Cover & Field Photos
          </h3>

          <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-emerald-400 transition-all bg-emerald-50/20">
            <input
              type="file"
              id="farm-images"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            <label htmlFor="farm-images" className="cursor-pointer space-y-2 block">
              <Upload className="w-10 h-10 text-emerald-600 mx-auto animate-bounce" />
              <p className="font-extrabold text-sm text-gray-800">Click to Upload Farm Cover & Photos</p>
              <p className="text-xs text-gray-400">Supports PNG, JPG, JPEG, WebP images</p>
            </label>
          </div>

          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
              {imagePreviews.map((src, idx) => (
                <div key={idx} className="relative group rounded-xl overflow-hidden border border-gray-200 h-28 bg-gray-100">
                  <img src={src} alt={`Preview ${idx}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full opacity-80 hover:opacity-100 transition-all"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/farm-operations')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-xl text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-xl shadow-md transition-all text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating Farm Project...
              </>
            ) : (
              <>
                <Sprout className="w-5 h-5" /> Save & Launch Farm Project
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateFarmProject;
